import { Controller } from "@hotwired/stimulus"

// Pilote l'assistant mono-page : ne montre que les étapes pertinentes pour la
// famille choisie, gère précédent/suivant, la progression et l'état des boutons.
export default class extends Controller {
  static targets = ["form", "step", "progress", "prevBtn", "nextBtn", "submitBtn"]
  static values = { family: String }

  connect() {
    this.index = 0
    this.family = this.familyValue || null
    this.render()
    if (this.family) this.next() // famille pré-sélectionnée : saute à la 1re question
  }

  get activeSteps() {
    return this.stepTargets.filter((step) => {
      if (step.dataset.step === "family") return true
      return step.dataset.family === this.family
    })
  }

  // Choix unique : sélectionner une famille passe directement à la 1re question.
  selectFamily(event) {
    this.family = event.target.value
    this.index = 0
    this.render()
    this.next()
  }

  next() {
    if (!this.validateCurrent()) return
    if (this.index < this.activeSteps.length - 1) {
      this.index++
      this.render()
    }
  }

  // Choix unique sur une question : passe à l'écran suivant, ou soumet
  // directement si c'est la dernière étape (cas des familles sans étapes après).
  advance() {
    if (this.isLastStep) {
      if (this.validateCurrent()) this.formTarget.requestSubmit()
    } else {
      this.next()
    }
  }

  get isLastStep() {
    return this.family !== null && this.index === this.activeSteps.length - 1
  }

  prev() {
    if (this.index > 0) {
      this.index--
      this.render()
    }
  }

  validateCurrent() {
    const current = this.activeSteps[this.index]

    // Groupes de boutons radio required : au moins un coché par nom.
    const requiredRadios = [...current.querySelectorAll("input[type='radio'][required]")]
    const names = new Set(requiredRadios.map((i) => i.name))
    for (const name of names) {
      const group = current.querySelectorAll(`input[name="${CSS.escape(name)}"]`)
      if (![...group].some((i) => i.checked)) {
        group[0]?.reportValidity?.()
        return false
      }
    }

    // Autres champs required (texte, zone de texte) : validation native.
    for (const field of current.querySelectorAll("input[required]:not([type='radio']), textarea[required], select[required]")) {
      if (!field.reportValidity()) return false
    }

    return true
  }

  render() {
    const steps = this.activeSteps

    // Visibilité : une seule étape affichée à la fois.
    this.stepTargets.forEach((step) => step.classList.add("hidden"))
    steps[this.index]?.classList.remove("hidden")

    // Soumission : on désactive les fieldsets des familles non sélectionnées
    // pour les exclure du POST (évite les clés en double comme answers[context]
    // partagées entre familles, et les champs masqués déjà remplis). Toutes les
    // étapes de la famille active restent activées même si masquées.
    this.stepTargets.forEach((step) => {
      const active = step.dataset.step === "family" || step.dataset.family === this.family
      step.disabled = !active
    })

    // L'étape famille seule (avant sélection) est aussi la "dernière" ; le garde
    // this.family !== null (dans isLastStep) empêche d'afficher le bouton de
    // soumission prématurément.
    const onFirst = this.index === 0
    const onLast = this.isLastStep

    this.prevBtnTarget.classList.toggle("hidden", onFirst)
    this.nextBtnTarget.classList.toggle("hidden", onLast)
    this.submitBtnTarget.classList.toggle("hidden", !onLast)

    // Progression : 0 % sur la 1re étape (avant tout choix), 100 % à la dernière.
    const total = steps.length
    const progress = total <= 1 ? 0 : (this.index / (total - 1)) * 100
    this.progressTarget.style.width = `${progress}%`
  }
}
